import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery } from "@mui/material";
import { Container } from "./Container";
import { scrollToSectionId } from "../modules/utils";

const NavWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--nav-h);
  z-index: 50;

  background: rgba(11, 12, 18, 0.68);
  border-bottom: 1px solid var(--border-0);
  backdrop-filter: blur(14px);

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(185, 255, 102, 0.5),
      rgba(124, 92, 255, 0.55),
      transparent
    );
    opacity: 0.35;
    pointer-events: none;
  }
`;

const NavInner = styled.div`
  height: var(--nav-h);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Brand = styled.button`
  background: transparent;
  border: 0;
  padding: 8px 10px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;

  font-family: Modak, sans-serif;
  text-transform: uppercase;
  letter-spacing: 3px;
  font-weight: 100;
  color: var(--text-0);

  transition: background 160ms ease, transform 160ms ease;

  &:hover {
    background: var(--surface-0);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

const NavGroup = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;

  @media screen and (max-width: 860px) {
    display: none;
  }
`;

const NavItem = styled.button`
  appearance: none;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-0);
  cursor: pointer;
  user-select: none;

  font-weight: 650;
  letter-spacing: 0.02em;
  padding: 10px 12px;
  border-radius: 999px;

  transition: background 160ms ease, border-color 160ms ease,
    transform 160ms ease;

  &:hover {
    background: var(--surface-0);
    border-color: rgba(255, 255, 255, 0.12);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

const MobileToggle = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: var(--surface-0);
  color: var(--text-0);

  display: none;
  align-items: center;
  justify-content: center;

  height: 40px;
  width: 40px;
  border-radius: 12px;
  cursor: pointer;

  transition: background 160ms ease, transform 160ms ease,
    border-color 160ms ease;

  &:hover {
    background: var(--surface-1);
    border-color: rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  @media screen and (max-width: 860px) {
    display: inline-flex;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 49;

  display: flex;
  justify-content: center;
  align-items: flex-start;

  padding: calc(var(--nav-h) + 12px) var(--gutter) 12px;

  background: rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(8px);

  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 220ms ease;

  @media screen and (min-width: 861px) {
    display: none;
  }
`;

const MobilePanel = styled.div`
  width: 100%;
  max-width: 520px;

  border: 1px solid var(--border-0);
  background: rgba(18, 19, 27, 0.88);
  box-shadow: var(--shadow-0);
  border-radius: var(--radius-lg);
  padding: 14px;

  transform: translateY(${({ $open }) => ($open ? "0" : "-6px")});
  transition: transform 220ms ease;
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileNavItem = styled(NavItem)`
  width: 100%;
  justify-content: flex-start;
  font-size: 18px;
  padding: 14px 14px;
  border-color: rgba(255, 255, 255, 0.12);
  background: var(--surface-0);

  &:hover {
    transform: none;
  }
`;

const LINKS = [
  { label: "About", id: "about-2" },
  { label: "The Work", id: "work" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const isMobile = useMediaQuery(`(max-width: 860px)`);
  const [open, setOpen] = useState(false);
  const links = useMemo(() => LINKS, []);

  useEffect(() => {
    if (!isMobile && open) setOpen(false);
  }, [isMobile, open]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const scrollTo = (id) => {
    scrollToSectionId(id, -72);
    setOpen(false);
  };

  return (
    <>
      <NavWrapper>
        <Container>
          <NavInner>
            <Brand onClick={() => scrollTo("about-1")} aria-label="Go to top">
              Auxiliary One
            </Brand>

            <NavGroup aria-label="Primary navigation">
              {links.map((link) => (
                <NavItem key={link.id} onClick={() => scrollTo(link.id)}>
                  {link.label}
                </NavItem>
              ))}
            </NavGroup>

            <MobileToggle
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </MobileToggle>
          </NavInner>
        </Container>
      </NavWrapper>

      <MobileOverlay $open={open} onClick={() => setOpen(false)}>
        <MobilePanel $open={open} onClick={(e) => e.stopPropagation()}>
          <MobileNav aria-label="Mobile navigation">
            {links.map((link) => (
              <MobileNavItem key={link.id} onClick={() => scrollTo(link.id)}>
                {link.label}
              </MobileNavItem>
            ))}
          </MobileNav>
        </MobilePanel>
      </MobileOverlay>
    </>
  );
};
